import { get as lodashGet } from "lodash";
import { ActionContext } from "vuex";
import { GenerateStripeStateOptions } from "@/types/store";
import axios from "@/plugins/axios";
import { AxiosResponse } from "axios";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const add = (property: string) => (state: any, payload: any) => {
  state[property].push(payload);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const set = (property: string) => (state: any, payload: any) =>
  (state[property] = payload);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const get = (propertyPath: string, defaultValue?: any) => (state: any) =>
  lodashGet(state, propertyPath, defaultValue);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const toggle = (property: string) => (state: any) =>
  (state[property] = !state[property]);

export const generateStripeState = (
  options: GenerateStripeStateOptions
): any => {
  const {
    state: givenState = {},
    getters,
    actions,
    mutationKey,
    api,
  } = options;

  const { collectionName } = givenState;
  const {
    getPagination,
    getCollection,
    getEntity,
    getLoading,
    getListLoading,
    getCreateLoading,
    getUpdateLoading,
    getDeleteLoading,
  } = getters;

  const {
    get: getAction,
    list: listAction,
    search: searchAction,
    update: updateAction,
    delete: deleteAction,
    create: createAction,
  } = actions;

  const {
    get: getApi,
    list: listApi,
    search: searchApi,
    update: updateApi,
    delete: deleteApi,
    create: createApi,
  } = api;

  const mutations = {
    pagination: {
      limit: `${mutationKey}_LIMIT`,
      has_more: `${mutationKey}_HAS_MORE`,
      starting_after: `${mutationKey}_STARTING_AFTER`,
      ending_before: `${mutationKey}_ENDING_BEFORE`,
    },
    update: `${mutationKey}_UPDATE`,
    set: `${mutationKey}_SET`,
    delete: `${mutationKey}_DELETE}`,
    getLoading: `${mutationKey}_LOADING`,
    listLoading: `${mutationKey}_LIST_LOADING`,
    createLoading: `${mutationKey}_CREATE_LOADING`,
    updateLoading: `${mutationKey}_UPDATE_LOADING`,
    deleteLoading: `${mutationKey}_DELETE_LOADING`,
  };

  const state: any = {};
  const getterMethods: any = {};
  const mutationMethods: any = {};
  const actionMethods: any = {};

  if (getEntity) {
    getterMethods[getEntity] = (state: any) => (id: string) =>
      state[collectionName][id];
  }

  if (getLoading && getApi && getAction) {
    state.stripeGetLoading = false;
    getterMethods[getLoading] = (state: any) => state.stripeGetLoading;
    mutationMethods[mutations.getLoading] = (state: any, value: boolean) => {
      state.stripeGetLoading = value;
    };
    actionMethods[getAction] = async (
      context: ActionContext<any, any>,
      data: any
    ): Promise<any> => {
      // You can supply a `_reload: true` param if you want to fetch from the API
      // instead of from the state (if it exists)
      const { _reload, id, expand, _headers = {} } = data || {};

      const payload: any = {
        id,
        headers: _headers,
      };

      if (expand) {
        payload.params = {};
        payload.params.expand = expand;
      }

      context.commit(mutations.getLoading, true);

      const fetchEntity = async () =>
        await axios
          .get(getApi(data), payload)
          .then((res) => {
            context.commit(mutations.update, res.data);
            return res;
          })
          .catch((e: AxiosResponse) => {
            context.commit(
              "snackbar/toastError",
              { message: e.data },
              { root: true }
            );
            return e;
          })
          .finally(() => {
            context.commit(mutations.getLoading, false);
          });

      const inCollection = id in context.state[collectionName];

      if (!_reload && inCollection) {
        context.commit(mutations.getLoading, false);

        // Mocking the response object with data & status params
        return { data: context.state[collectionName][id], status: 200 };
      } else {
        return fetchEntity();
      }
    };
  }

  if (getListLoading && getPagination && listApi && listAction) {
    state.stripeListLoading = false;
    state.pagination = {};
    state.pagination.has_more = false;
    state.pagination.limit = 25;
    state.pagination.starting_after = undefined;
    state.pagination.ending_before = undefined;
    getterMethods[getListLoading] = (state: any) => state.stripeListLoading;
    getterMethods[getPagination] = (state: any) => state.pagination;
    mutationMethods[mutations.listLoading] = (state: any, value: boolean) => {
      state.stripeListLoading = value;
    };
    mutationMethods[mutations.pagination.limit] = (
      state: any,
      limit: number
    ) => {
      state.pagination.limit = limit;
    };
    mutationMethods[mutations.pagination.has_more] = (
      state: any,
      hasMore: boolean
    ) => {
      state.pagination.has_more = hasMore;
    };
    mutationMethods[mutations.pagination.starting_after] = (
      state: any,
      id: string
    ) => {
      state.pagination.starting_after = id;
    };
    mutationMethods[mutations.pagination.ending_before] = (
      state: any,
      id: string
    ) => {
      state.pagination.ending_before = id;
    };
    actionMethods[listAction] = (
      context: ActionContext<any, any>,
      payload: any
    ): Promise<any> => {
      context.commit(mutations.set, []);
      const { type, query = {}, _headers: headers = {} } = payload;
      const { limit, starting_after, ending_before } = context.state.pagination;

      const apiOptions = {
        params: { limit, ...query },
        headers,
      };

      context.commit(mutations.pagination.limit, apiOptions.params.limit);

      if (type === "next") {
        apiOptions.params.starting_after = starting_after;
      } else if (type === "prev") {
        apiOptions.params.ending_before = ending_before;
      }

      const processResults = (res: AxiosResponse) => {
        const { has_more, data } = res.data;
        const lastElement = data.slice(-1).pop() || {};
        const firstElement = data[0] || {};
        const { id: lastId } = lastElement;
        const { id: firstId } = firstElement;

        context.commit(mutations.set, data);
        context.commit(mutations.pagination.has_more, !!has_more);

        if (has_more) {
          context.commit(mutations.pagination.starting_after, lastId);
          context.commit(mutations.pagination.ending_before, firstId);
        } else if (type === "next") {
          context.commit(mutations.pagination.ending_before, firstId);
          context.commit(mutations.pagination.starting_after, undefined);
        } else if (type === "prev") {
          context.commit(mutations.pagination.ending_before, undefined);
        }

        return res;
      };

      context.commit(mutations.listLoading, true);

      return axios
        .get(listApi(query), apiOptions)
        .then(processResults)
        .catch((e: AxiosResponse) => {
          context.commit(
            "snackbar/toastError",
            { message: e.data },
            { root: true }
          );
          return e;
        })
        .finally(() => {
          context.commit(mutations.listLoading, false);
        });
    };
  }

  if (getListLoading && getPagination && searchApi && searchAction) {
    state.stripeListLoading = false;
    state.pagination = {};
    state.pagination.has_more = false;
    state.pagination.limit = 25;
    state.pagination.search_current_page = null;
    state.pagination.search_history = [];
    getterMethods[getListLoading] = (state: any) => state.stripeListLoading;
    getterMethods[getPagination] = (state: any) => state.pagination;
    mutationMethods[mutations.listLoading] = (state: any, value: boolean) => {
      state.stripeListLoading = value;
    };
    mutationMethods[mutations.pagination.limit] = (
      state: any,
      limit: number
    ) => {
      state.pagination.limit = limit;
    };
    mutationMethods[mutations.pagination.has_more] = (
      state: any,
      hasMore: boolean
    ) => {
      state.pagination.has_more = hasMore;
    };
    mutationMethods[`${mutationKey}_CURRENT_PAGE`] = (
      state: any,
      currentPage: string
    ) => {
      state.pagination.search_current_page = currentPage;
    };
    mutationMethods[`RECORD_${mutationKey}_SEARCH_HISTORY`] = (
      state: any,
      page: string
    ) => {
      // Prevent duplicates by using Set
      state.pagination.search_history = Array.from(
        new Set([...state.pagination.search_history, page])
      );
    };
    actionMethods[searchAction] = (
      context: ActionContext<any, any>,
      payload: any
    ): Promise<any> => {
      context.commit(mutations.set, []);
      const { query = {}, _headers: headers = {}, type } = payload;
      const { limit, search_history, search_current_page } =
        context.state.pagination;

      const apiOptions: any = {
        params: { limit },
        headers,
      };

      context.commit(mutations.pagination.limit, apiOptions.params.limit);

      const pageIndex = search_history.indexOf(search_current_page);

      if (type === "next") {
        apiOptions.params.page =
          pageIndex === -1 && search_history[pageIndex + 1]
            ? search_history[0]
            : search_history[pageIndex + 1];
      } else if (type === "prev") {
        apiOptions.params.page =
          pageIndex === -1 && search_history[pageIndex - 1]
            ? search_history[0]
            : search_history[pageIndex - 1];
      }

      if (type === "next" || type === "prev") {
        context.commit(`${mutationKey}_CURRENT_PAGE`, apiOptions.params.page);
      }

      const processResults = (res: AxiosResponse) => {
        const { has_more, data, next_page } = res.data;

        context.commit(mutations.set, data);
        context.commit(mutations.pagination.has_more, !!has_more);

        if (has_more) {
          // Only record a next page if there is one
          if (next_page && type !== "prev")
            context.commit(`RECORD_${mutationKey}_SEARCH_HISTORY`, next_page);
        }

        return res;
      };

      context.commit(mutations.listLoading, true);

      return axios
        .post(searchApi(query), { query }, apiOptions)
        .then(processResults)
        .catch((e: AxiosResponse) => {
          context.commit(
            "snackbar/toastError",
            { message: e.data },
            { root: true }
          );
          return e;
        })
        .finally(() => {
          context.commit(mutations.listLoading, false);
        });
    };
  }

  if (getCreateLoading && createApi && createAction) {
    state.stripeCreateLoading = false;
    getterMethods[getCreateLoading] = (state: any) => state.stripeCreateLoading;
    mutationMethods[mutations.createLoading] = (state: any, value: boolean) => {
      state.stripeCreateLoading = value;
    };
    actionMethods[createAction] = (
      context: ActionContext<any, any>,
      data: any
    ): Promise<any> => {
      const { _headers: headers = {} } = data;
      const payload = { ...data };
      const config = { headers };

      context.commit(mutations.createLoading, true);

      return axios
        .post(createApi(payload), payload, config)
        .then((res) => {
          context.commit(mutations.update, res.data);
          return res;
        })
        .catch((e: AxiosResponse) => {
          context.commit(
            "snackbar/toastError",
            { message: e.data },
            { root: true }
          );
          return e;
        })
        .finally(() => {
          context.commit(mutations.createLoading, false);
        });
    };
  }

  if (getUpdateLoading && updateApi && updateAction) {
    state.stripeUpdateLoading = false;
    getterMethods[getUpdateLoading] = (state: any) => state.stripeUpdateLoading;
    mutationMethods[mutations.updateLoading] = (state: any, value: boolean) => {
      state.stripeUpdateLoading = value;
    };
    actionMethods[updateAction] = (
      context: ActionContext<any, any>,
      data: any
    ) => {
      const { _headers: headers = {} } = data;
      const payload = { ...data };
      const config = { headers };

      context.commit(mutations.updateLoading, true);

      return axios
        .patch(updateApi(payload), payload, config)
        .then((res: AxiosResponse) => {
          context.commit(mutations.update, res.data);
          return res;
        })
        .catch((e: AxiosResponse) => {
          context.commit(
            "snackbar/toastError",
            { message: e.data },
            { root: true }
          );
          return e;
        })
        .finally(() => {
          context.commit(mutations.updateLoading, false);
        });
    };
  }

  if (getDeleteLoading && deleteApi && deleteAction) {
    state.stripeDeleteLoading = false;
    getterMethods[getDeleteLoading] = (state: any) => state.stripeDeleteLoading;
    mutationMethods[mutations.deleteLoading] = (state: any, value: boolean) => {
      state.stripeDeleteLoading = value;
    };
    actionMethods[deleteAction] = (
      context: ActionContext<any, any>,
      data: any
    ) => {
      const { _headers: headers = {} } = data;
      const payload = { ...data };
      const config = {
        headers,
        data: payload,
      };

      context.commit(mutations.deleteLoading, true);

      return axios
        .delete(deleteApi(payload), config)
        .then((res) => {
          context.commit(mutations.delete, res.data);
          return res;
        })
        .catch((e) => {
          context.commit(
            "snackbar/toastError",
            { message: e.data },
            { root: true }
          );
          return e;
        })
        .finally(() => {
          context.commit(mutations.deleteLoading, false);
        });
    };
  }

  return {
    state: {
      ...state,
      [collectionName]: {},
    },
    getters: {
      ...getterMethods,
      [getCollection]: (state: any) => Object.values(state[collectionName]),
    },
    actions: {
      ...actionMethods,
    },
    mutations: {
      ...mutationMethods,
      [mutations.delete](state: any, payload: any) {
        const { id } = payload || {};

        if (id) {
          const stateClone = { ...state[collectionName] };
          delete stateClone[id];
          state[collectionName] = stateClone;
        }
      },
      [mutations.update](state: any, payload: any) {
        const { id } = payload || {};

        if (id) {
          const newStateCollection: any = {};
          // We need to add first because is not able to sort this kind of object or "map"
          // This will be showing the new element in the beginning of the list
          newStateCollection[id] = payload;
          Object.entries({ ...state[collectionName] }).forEach((entry) => {
            // To not overwrite the new object with old one
            if (id !== entry[0]) {
              newStateCollection[entry[0]] = entry[1];
            }
          });
          state[collectionName] = newStateCollection;
        }
      },
      [mutations.set](state: any, items: any[]) {
        state[collectionName] = items.reduce((acc, current) => {
          acc[current.id] = current;

          return acc;
        }, {});
      },
    },
  };
};
